import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { useDebounce as usePrimereactDebounce } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import { useDebounce } from '../../hooks';
import { TaskHistoryProps } from '../../types/props';
import { clearFilter } from '../../utils';
import { formatTimestamp } from '../../utils/date-utils';

// Define a new type for the parsed task history data
interface ParsedTaskHistory {
  id: string;
  user: string | number;
  modificationTime: string;
  action: string;
  description: string;
}

const TaskHistoryComponent: React.FC<TaskHistoryProps> = ({
  taskHistory,
  loading
}) => {
  const [scrollHeight, setScrollHeight] = useState('30wh');
  const dt = useRef<DataTable<ParsedTaskHistory[]>>(null);
  const toast = useRef<Toast>(null);
  const dataTableRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLInputElement>(null);
  const modificationTimeRef = useRef<HTMLInputElement>(null);
  const actionRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  
  const [showStatusChangesOnly, setShowStatusChangesOnly] = useState(false);
  
  const [filters, debouncedFilters, setFilters] = usePrimereactDebounce<Record<string, DataTableFilterMetaData>>(
    {
      user: { value: null, matchMode: FilterMatchMode.CONTAINS },
      modificationTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
      action: { value: null, matchMode: FilterMatchMode.CONTAINS },
      description: { value: null, matchMode: FilterMatchMode.CONTAINS }
    },
    500
  );

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setMultiSortMeta(e.multiSortMeta);
  }, []);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const toggleStatusFilter = () => {
    setShowStatusChangesOnly(!showStatusChangesOnly);
  };

  const toggleActionSort = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentSortMeta = multiSortMeta || [];
    const actionSortIndex = currentSortMeta.findIndex(
      (sort) => sort.field === 'action'
    );

    let newSortMeta: DataTableSortMeta[];

    if (actionSortIndex === -1) {
      newSortMeta = [...currentSortMeta, { field: 'action', order: 1 }];
    } else {
      const currentOrder = currentSortMeta[actionSortIndex].order;
      if (currentOrder === 1) {
        newSortMeta = [...currentSortMeta];
        newSortMeta[actionSortIndex] = { field: 'action', order: -1 };
      } else {
        newSortMeta = currentSortMeta.filter((_, index) => index !== actionSortIndex);
        if (newSortMeta.length === 0) {
          newSortMeta = undefined as any;
        }
      }
    }

    setMultiSortMeta(newSortMeta);
  };

  useEffect(() => {
    const calculateHeight = () => {
      if (dataTableRef.current) {
        const tableElement = dataTableRef.current;
        if (tableElement) {
          const tableOffset =
            tableElement.getBoundingClientRect().top + window.pageYOffset;
          const calculatedHeight = `calc(100vh - ${
            tableOffset - 18 + (Object.keys(taskHistory).length > 0 ? 60 : 0)
          }px)`;
          setScrollHeight(calculatedHeight);
        }
      }
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, [taskHistory]);

  // Utility function to replace '->' with an icon
  const replaceArrowWithIcon = (text: string) => {
    const parts = text.split('->');
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < parts.length - 1 && (
          <i className="pi pi-arrow-right" style={{ margin: '0 0.5rem' }}></i>
        )}
      </React.Fragment>
    ));
  };

  const parseChanges = (
    changes: string | string[]
  ): { action: string; description: string }[] => {
    if (typeof changes === 'string') {
      changes = [changes];
    }
    return changes.map((change) => {
      const splitValue = change.includes('\n') ? '\n' : '.';
      const parts = change.split(splitValue);
      return {
        action: parts[0].trim(),
        description: parts.slice(1).join(splitValue).trim() || 'Нет описания'
      };
    });
  };

  const taskHistoryArray: ParsedTaskHistory[] = Object.keys(taskHistory)
    .map((key) => {
      const parsedChanges = parseChanges(taskHistory[key].changes);
      return parsedChanges.map((parsedChange, index) => {
        const newDate = new Date(taskHistory[key].modificationTime);
        newDate.setHours(newDate.getHours());
        return {
          id: `${key}-${index}`,
          user: taskHistory[key].user,
          modificationTime: formatTimestamp(newDate.getTime()),
          action: parsedChange.action,
          description: parsedChange.description
        };
      });
    })
    .flat();

  const filteredTaskHistory = showStatusChangesOnly
    ? taskHistoryArray.filter(history => 
      history.action.includes('Изменен статус наряда') || 
        history.action.includes('Статус изменен')
    )
    : taskHistoryArray;

  const descriptionBody = (history: ParsedTaskHistory) => {
    return replaceArrowWithIcon(history.description);
  };

  const actionHeaderTemplate = () => {
    return (
      <div className="flex align-items-center gap-2">
        <span
          onClick={toggleActionSort}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          Действие
        </span>
        <Button
          id="statusFilterBtn"
          type="button"
          icon={showStatusChangesOnly ? 'pi pi-filter-fill' : 'pi pi-filter'}
          className={`p-button-text p-button-sm ${showStatusChangesOnly ? 'p-button-info' : 'p-button-secondary'}`}
          onClick={toggleStatusFilter}
          tooltip="Показать только изменения статусов"
          tooltipOptions={{ position: 'top' }}
        />
        <Tooltip target="#statusFilterBtn" />
      </div>
    );
  };

  return (
    <div
      ref={dataTableRef}
      style={{
        width: '100%',
        overflow: 'auto',
        border: '1px solid rgb(211, 211, 211)',
        borderRadius: '5px',
        minHeight: '42px',
        alignContent: 'center',
        padding: 0
      }}
      className="taskHistory-table wrapper wrapper--main"
    >
      <Toast ref={toast} />
      <DataTable
        ref={dt}
        scrollHeight={scrollHeight}
        selectionMode="single"
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        size="small"
        value={filteredTaskHistory}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        emptyMessage={
          filteredTaskHistory.length > 0
            ? 'Изменения не найдены.'
            : showStatusChangesOnly 
              ? 'Изменения статусов не найдены.'
              : 'Наряд не зарегистрирован.'
        }
        filters={debouncedFilters}
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
      >
        <Column
          columnKey="user"
          field="user"
          header="Пользователь"
          style={{ width: '15%', minWidth: '15%' }}
          filterElement={
            <div className="flex justify-content-end">
              <InputText ref={userRef} onChange={handleInputChange('user')} />
            </div>
          }
          onFilterClear={() =>
            clearFilter('user', filters, setFilters, userRef)
          }
          filter
          sortable
        />
        <Column
          field="modificationTime"
          filterField="modificationTime"
          sortField="modificationTime"
          header="Дата и время"
          style={{ width: '8%', minWidth: '8%', wordBreak: 'break-word' }}
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={modificationTimeRef}
                onChange={handleInputChange('modificationTime')}
              />
            </div>
          }
          onFilterClear={() =>
            clearFilter(
              'modificationTime',
              filters,
              setFilters,
              modificationTimeRef
            )
          }
          filter
          sortable
        />
        <Column
          field="action"
          filterField="action"
          sortField="action"
          header={actionHeaderTemplate}
          style={{ width: '15%', minWidth: '15%', wordBreak: 'break-word' }}
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={actionRef}
                onChange={handleInputChange('action')}
              />
            </div>
          }
          onFilterClear={() =>
            clearFilter('action', filters, setFilters, actionRef)
          }
          filter
          sortable
        />
        <Column
          columnKey="description"
          field="description"
          header="Описание"
          body={descriptionBody}
          style={{ width: '28%', minWidth: '28%', wordBreak: 'break-word' }}
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionRef}
                onChange={handleInputChange('description')}
              />
            </div>
          }
          onFilterClear={() =>
            clearFilter('description', filters, setFilters, descriptionRef)
          }
          filter
          sortable
        />
      </DataTable>
    </div>
  );
};

export default TaskHistoryComponent;